// src/admin/AdminDashboard.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest, clearAuthToken, getAuthToken } from "../services/apiClient";
import API_BASE from "../services/apiClient";
import AdminProductMeta from "./AdminProductMeta";
import AdminCustomProducts from "./AdminCustomProducts";
import AdminBlogs from "./AdminBlogs";
import AdminMessages from "./AdminMessages";
import AdminOrders from "./AdminOrders";
import AdminCustomOrders from "./AdminCustomOrders";
import AdminSavedCarts from "./AdminSavedCarts";
import AdminInvoices from "./AdminInvoices";
import AdminQuotations from "./AdminQuotations";
import AdminHeroSlides from "./AdminHeroSlides";
import AdminPartners from "./AdminPartners";
import AdminSocialDetails from "./AdminSocialDetails";
import AdminEmailSettings from "./AdminEmailSettings";
import AdminSupplierSettings from "./AdminSupplierSettings";
import AdminStripeSettings from "./AdminStripeSettings";
import AdminSiteContent from "./AdminSiteContent";
import AdminDatabaseHealth from "./AdminDatabaseHealth";
import AdminAccount from "./AdminAccount";
import AdminUsers from "./AdminUsers";
import AdminVisitors from "./AdminVisitors";
import AdminSendOffers from "./AdminSendOffers";
import AdminCoupons from "./AdminCoupons";
import AdminHomeCategorySection from "./AdminHomeCategorySection";
import "./AdminDashboard.css";

const MENU_GROUPS = [
  { label: "Main", items: [
  { key: "dashboard",    label: "Dashboard",       icon: "📊" },
  { key: "productMeta",  label: "Products & Cats",  icon: "🖨️" },
  { key: "customProducts", label: "Promotional Products",   icon: "🎁" },
  { key: "blogs",         label: "Blogs",             icon: "📝" },
  { key: "orders",       label: "Orders",           icon: "📦" },
  { key: "savedCarts",   label: "Saved Carts",      icon: "🛒" },
  { key: "customOrders", label: "Custom Orders",    icon: "🧾" },
  { key: "invoices", label: "Invoices", icon: "🧮" },
  { key: "quotations", label: "Quotations", icon: "📄" },
  { key: "users", label: "Users", icon: "👥" },
  { key: "messages",     label: "Messages",         icon: "✉️" },
  { key: "sendOffers",   label: "Send Offers",      icon: "📧" },
  { key: "coupons",      label: "Coupons",          icon: "🏷️" },
  { key: "partners",     label: "Partners",         icon: "🤝" },
  { key: "database", label: "Database Health", icon: "🗄️" },
  { key: "account", label: "Admin Account", icon: "👤" },
  { key: "visitors", label: "Visitors Online", icon: "🟢" },
  ]},
  { label: "System Settings", items: [
    { key: "emailSettings", label: "Email & SMTP", icon: "✉️" },
    { key: "supplierSettings", label: "Supplier API", icon: "🔐" },
    { key: "stripeSettings", label: "Stripe", icon: "💳" },
  ]},
  { label: "Site Settings", items: [
    { key: "hero", label: "Hero Slides", icon: "🖼️" },
    { key: "homeCategories", label: "Homepage Product Categories", icon: "🗂️" },
    { key: "social", label: "Social & Contact Details", icon: "🌐" },
    { key: "siteContent", label: "Site Content & Email Templates", icon: "✍️" },
  ]},
];
const MENU = MENU_GROUPS.flatMap(group => group.items);

export default function AdminDashboard() {
  const [user, setUser]         = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [orderFilter, setOrderFilter] = useState("all");
  const [productMetaMode, setProductMetaMode] = useState("products");
  const navigate = useNavigate();

  useEffect(() => {
    if (!getAuthToken()) { navigate("/admin/login"); return; }
    apiRequest("/auth/me")
      .then(result => setUser(result.user))
      .catch(error => {
        // Only sign out when the server rejects the token, not on network/server hiccups.
        if (error.status === 401 || error.status === 403) { clearAuthToken(); navigate("/admin/login?error=unauthorized"); }
        else setUser({ email: "", displayName: "Admin", isAdmin: true });
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  useEffect(() => {
    apiRequest("/admin/settings/adminTheme").then(theme => {
      const root = document.documentElement;
      if (theme.adminPrimaryColor) root.style.setProperty("--cyan", theme.adminPrimaryColor);
      if (theme.adminBackgroundColor) root.style.setProperty("--adm-bg", theme.adminBackgroundColor);
      if (theme.adminSidebarColor) root.style.setProperty("--adm-sidebar", theme.adminSidebarColor);
      if (theme.adminCardColor) root.style.setProperty("--adm-card", theme.adminCardColor);
      if (theme.adminTextColor) root.style.setProperty("--adm-text", theme.adminTextColor);
      if (theme.adminSidebarTextColor) root.style.setProperty("--adm-sidebar-text", theme.adminSidebarTextColor);
      if (theme.adminMutedTextColor) root.style.setProperty("--adm-muted", theme.adminMutedTextColor);
      if (theme.adminProductGridBgColor) root.style.setProperty("--adm-products-grid-bg", theme.adminProductGridBgColor);
      if (theme.adminProductListBgColor) root.style.setProperty("--adm-products-list-bg", theme.adminProductListBgColor);
    }).catch(() => {});
  }, []);

  const handleSignOut = () => { clearAuthToken(); navigate("/admin/login"); };

  if (checking) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f1117", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
        <div style={{ width: 36, height: 36, border: "3px solid rgba(255,255,255,0.1)", borderTop: "3px solid var(--cyan)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Verifying access...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="adm-layout">
      <aside className="adm-sidebar">
        <div className="adm-brand">
          <span className="adm-brand-name">CIRCUIT</span>
          <span className="adm-brand-sub">Admin Panel</span>
        </div>
        <nav className="adm-nav">
          {MENU_GROUPS.map(group => (
            <div key={group.label}>
              <div className="adm-nav-group-label">{group.label}</div>
              {group.items.map(m => (
                <button key={`${group.label}-${m.key}`}
                  className={`adm-nav-btn ${activeTab === m.key ? "active" : ""}`}
                  onClick={() => setActiveTab(m.key)}>
                  <span>{m.icon}</span> {m.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="adm-sidebar-footer">
          {user && <p className="adm-user">✅ {user.email}</p>}
          <button className="adm-signout" onClick={handleSignOut}>Sign Out</button>
        </div>
      </aside>

      <main className="adm-main">
        <div className="adm-topbar">
          <h1 className="adm-page-title">
            {MENU.find(m => m.key === activeTab)?.icon}{" "}
            {MENU.find(m => m.key === activeTab)?.label}
          </h1>
        </div>
        <div className="adm-content">
          {activeTab === "dashboard"    && <AdminOverview onSelectTab={(tab, filterOrMode) => { setOrderFilter(filterOrMode || "all"); setProductMetaMode(filterOrMode || "products"); setActiveTab(tab); }} />}
          {activeTab === "productMeta"  && <AdminProductMeta initialMode={productMetaMode} />}
          {activeTab === "customProducts" && <AdminCustomProducts />}
          {activeTab === "blogs" && <AdminBlogs />}
          {activeTab === "orders"       && <AdminOrders initialFilter={orderFilter} />}
          {activeTab === "customOrders" && <AdminCustomOrders />}
          {activeTab === "savedCarts" && <AdminSavedCarts />}
          {activeTab === "invoices" && <AdminInvoices />}
          {activeTab === "quotations" && <AdminQuotations />}
          {activeTab === "users" && <AdminUsers />}
          {activeTab === "messages"     && <AdminMessages />}
          {activeTab === "sendOffers"   && <AdminSendOffers />}
          {activeTab === "coupons"      && <AdminCoupons />}
          {activeTab === "hero"         && <AdminHeroSlides />}
          {activeTab === "homeCategories" && <AdminHomeCategorySection />}
          {activeTab === "partners"     && <AdminPartners />}
          {activeTab === "social"       && <AdminSocialDetails />}
          {activeTab === "emailSettings" && <AdminEmailSettings />}
          {activeTab === "supplierSettings" && <AdminSupplierSettings />}
          {activeTab === "stripeSettings" && <AdminStripeSettings />}
          {activeTab === "siteContent" && <AdminSiteContent />}
          {activeTab === "database" && <AdminDatabaseHealth />}
          {activeTab === "account" && <AdminAccount user={user} />}
          {activeTab === "visitors" && <AdminVisitors />}
        </div>
      </main>
    </div>
  );
}

function AdminOverview({ onSelectTab }) {
  const [stats, setStats] = useState({ orders: null, messages: null, partners: null, customOrders: null, productMeta: null });
  const [orderSummary, setOrderSummary] = useState(null);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [catalog, setCatalog] = useState({ products: null, categories: null });
  const [catalogSyncing, setCatalogSyncing] = useState(false);
  const [catalogSyncedAt, setCatalogSyncedAt] = useState(null);

  function syncCatalog() {
    setCatalogSyncing(true);
    fetch(`${API_BASE}/supplier/proxy?path=/product`)
      .then(res => res.json())
      .then(products => {
        const enabled = products.filter(p => p.enabled === 1);
        setCatalog({ products: enabled.length, categories: new Set(enabled.map(p => p.category)).size });
        setCatalogSyncedAt(new Date());
      })
      .catch(error => console.error("Catalog sync failed:", error))
      .finally(() => setCatalogSyncing(false));
  }

  useEffect(() => {
    apiRequest("/admin/stats")
      .then(setStats)
      .catch(error => console.error("Admin stats failed:", error));
    apiRequest("/admin/order-summary")
      .then(setOrderSummary)
      .catch(error => console.error("Order summary failed:", error));
    apiRequest("/admin/invoice-summary")
      .then(setInvoiceSummary)
      .catch(error => console.error("Invoice summary failed:", error));
    apiRequest("/admin/financial-summary")
      .then(setFinancialSummary)
      .catch(error => console.error("Financial summary failed:", error));
    syncCatalog();
  }, []);

  const attentionCards = [
    { key: "pending", label: "Orders Needing Attention", value: orderSummary?.needs_attention, color: "#f87171" },
    { key: "payments", label: "Payments Received (7 days)", value: orderSummary?.payments_received, color: "#4ade80" },
    { key: "supplier", label: "Sent to Supplier (7 days)", value: orderSummary?.sent_to_supplier, color: "#f5c200" },
    { key: "shipped", label: "Shipped Out (7 days)", value: orderSummary?.shipped_out, color: "#10d9a0" },
  ];

  const invoiceCards = [
    { label: "Total Invoice Value", value: invoiceSummary?.totalValueCents, color: "#00aadd", money: true },
    { label: "Invoices Paid", value: invoiceSummary?.paidInvoices, color: "#4ade80" },
    { label: "Invoices Pending", value: invoiceSummary?.pendingInvoices, color: "#f5c200" },
    { label: "Invoices Needing Attention", value: invoiceSummary?.needsAttention, color: "#f87171" },
  ];

  function openOrders(filter) {
    onSelectTab("orders", filter);
  }

  const CARDS = [
    { label: "Processing Orders", icon: "📦", color: "var(--yellow)",  value: stats.orders,       tab: "orders"      },
    { label: "New Custom Orders", icon: "🧾", color: "#8b5cf6",        value: stats.customOrders, tab: "customOrders"},
    { label: "New Messages",      icon: "✉️", color: "var(--magenta)", value: stats.messages,     tab: "messages"    },
    { label: "Products with Meta",icon: "🖨️", color: "var(--cyan)",    value: stats.productMeta,  tab: "productMeta" },
  ];

  return (
    <div className="adm-overview">
      <div style={attentionGrid}>
        <FinancialCard label="Total Sales" value={financialSummary?.total_sales_cents} color="#4ade80" />
        <FinancialCard label="Paid Orders" value={financialSummary?.paid_orders} color="#00aadd" raw />
        <FinancialCard label="Supplier Payable" value={financialSummary?.supplier_payable_cents} color="#f5c200" />
        <FinancialCard label="Estimated Profit" value={financialSummary?.estimated_profit_cents} color="#c084fc" />
      </div>
      <div style={attentionGrid}>
        {attentionCards.map(card => <button key={card.key} type="button" onClick={() => openOrders(card.key)} style={{ ...attentionCard, borderTopColor: card.color }}><strong style={{ color: card.color }}>{card.value === undefined || card.value === null ? "..." : card.value}</strong><span>{card.label}</span><small>Open orders</small></button>)}
      </div>
      <div style={attentionGrid}>
        {invoiceCards.map(card => <button key={card.label} type="button" onClick={() => onSelectTab("invoices")} style={{ ...attentionCard, borderTopColor: card.color }}><strong style={{ color: card.color }}>{card.value === undefined || card.value === null ? "..." : card.money ? `$${(Number(card.value) / 100).toFixed(2)} CAD` : card.value}</strong><span>{card.label}</span><small>Open invoices</small></button>)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
        <h3 style={{ color: "var(--adm-text)", fontSize: 15, margin: 0 }}>Product Catalog</h3>
        <button className="adm-btn" disabled={catalogSyncing} onClick={syncCatalog} style={{ padding: "5px 12px", fontSize: 12 }}>
          {catalogSyncing ? "Syncing..." : "🔄 Sync now"}
        </button>
        {catalogSyncedAt && <span style={{ color: "var(--adm-muted)", fontSize: 12 }}>Last synced: {catalogSyncedAt.toLocaleTimeString()}</span>}
      </div>
      <div className="adm-stat-cards">
        <div className="adm-stat-card" style={{ borderTopColor: "var(--cyan)", cursor: "pointer" }} onClick={() => onSelectTab("productMeta", "products")}>
          <span style={{ fontSize: 28 }}>📦</span>
          <strong style={{ color: "var(--cyan)", fontSize: 36 }}>{catalog.products ?? "…"}</strong>
          <span>Products</span>
        </div>
        <div className="adm-stat-card" style={{ borderTopColor: "#8b5cf6", cursor: "pointer" }} onClick={() => onSelectTab("productMeta", "categories")}>
          <span style={{ fontSize: 28 }}>🗂️</span>
          <strong style={{ color: "#8b5cf6", fontSize: 36 }}>{catalog.categories ?? "…"}</strong>
          <span>Categories</span>
        </div>
        <div className="adm-stat-card" style={{ borderTopColor: "#f5c200", cursor: "pointer" }} onClick={() => onSelectTab("productMeta", "bulk")}>
          <span style={{ fontSize: 28 }}>🖼️</span>
          <strong style={{ color: "#f5c200", fontSize: 36 }}>—</strong>
          <span>Bulk Category Image</span>
        </div>
        <div className="adm-stat-card" style={{ borderTopColor: "#10d9a0", cursor: "pointer" }} onClick={() => onSelectTab("productMeta", "customCats")}>
          <span style={{ fontSize: 28 }}>🧩</span>
          <strong style={{ color: "#10d9a0", fontSize: 36 }}>—</strong>
          <span>Custom Categories</span>
        </div>
      </div>
      <div className="adm-stat-cards">
        {CARDS.map(s => (
          <div key={s.label} className="adm-stat-card" style={{ borderTopColor: s.color, cursor: "pointer" }} onClick={() => onSelectTab(s.tab)}>
            <span style={{ fontSize: 28 }}>{s.icon}</span>
            <strong style={{ color: s.color, fontSize: 36 }}>
              {s.value === null ? "…" : s.value}
            </strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
      <p style={{ color: "rgba(255,255,255,0.4)", marginTop: 12, fontSize: 13 }}>
        Select a section from the sidebar to manage your content.
      </p>
    </div>
  );
}

function FinancialCard({ label, value, color, raw = false }) {
  const display = value === undefined || value === null ? "..." : raw ? String(value) : `$${(Number(value) / 100).toFixed(2)} CAD`;
  return <div style={{ ...attentionCard, borderTopColor: color, cursor: "default" }}><strong style={{ color }}>{display}</strong><span>{label}</span><small>From paid orders</small></div>;
}

const attentionGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14, marginBottom: 8 };
const attentionCard = { background: "var(--adm-card)", border: "1px solid var(--adm-border)", borderTop: "3px solid", borderRadius: 10, padding: "16px 18px", textAlign: "left", color: "#fff", cursor: "pointer", display: "flex", flexDirection: "column", gap: 6, fontFamily: "var(--font-main)" };
